import { useState, useCallback, useEffect } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import useEmblaCarousel from 'embla-carousel-react';
import { supabase } from '@/integrations/supabase/client';

export interface OnboardingSlide {
  icon: React.ElementType;
  secondaryIcons?: React.ElementType[];
  title: string;
  description: string;
  gradient: string;
}

interface PageOnboardingTooltipProps {
  storageKey: string;
  slides: OnboardingSlide[];
  dialogTitle?: string;
}

export const PageOnboardingTooltip = ({ storageKey, slides, dialogTitle }: PageOnboardingTooltipProps) => {
  // Start closed — only open after confirming NOT dismissed
  const [isOpen, setIsOpen] = useState(false);
  const [dbChecked, setDbChecked] = useState(false);

  useEffect(() => {
    // Fast path: already dismissed in localStorage
    if (localStorage.getItem(storageKey) === 'true') {
      setDbChecked(true);
      return;
    }

    const checkDismissed = async (userId: string) => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('dismissed_tooltips')
          .eq('user_id', userId)
          .single();

        const dismissed: string[] = (profile?.dismissed_tooltips as string[]) || [];
        if (dismissed.includes(storageKey)) {
          localStorage.setItem(storageKey, 'true');
        } else {
          setIsOpen(true);
        }
      } catch {
        // DB check failed — show tooltip as fallback
        setIsOpen(true);
      } finally {
        setDbChecked(true);
      }
    };

    // Try to get user; if not ready, listen for auth state change
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        checkDismissed(user.id);
      } else {
        // Wait for auth session
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
          if (session?.user) {
            subscription.unsubscribe();
            checkDismissed(session.user.id);
          }
        });
        // Timeout: if no session after 5s, show tooltip
        setTimeout(() => {
          subscription.unsubscribe();
          setDbChecked(prev => { if (!prev) setIsOpen(true); return true; });
        }, 5000);
      }
    });
  }, [storageKey]);

  const [emblaRef, emblaApi] = useEmblaCarousel({ watchDrag: true });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentSlide(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi, onSelect]);

  const handleDismiss = async () => {
    localStorage.setItem(storageKey, 'true');
    setIsOpen(false);

    // Persist to database
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('dismissed_tooltips')
        .eq('user_id', user.id)
        .single();

      const current: string[] = (profile?.dismissed_tooltips as string[]) || [];
      if (!current.includes(storageKey)) {
        await supabase
          .from('profiles')
          .update({ dismissed_tooltips: [...current, storageKey] })
          .eq('user_id', user.id);
      }
    } catch {
      // Silent fail — localStorage is the fallback
    }
  };

  if (!isOpen || !dbChecked) return null;

  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleDismiss(); }}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">{dialogTitle || 'Page Walkthrough'}</DialogTitle>
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {slides.map((slide, i) => {
              const Icon = slide.icon;
              return (
                <div key={i} className="min-w-0 shrink-0 grow-0 basis-full">
                  <div className="flex flex-col items-center px-8 pt-10 pb-6">
                    <div className={`relative flex items-center justify-center w-28 h-28 rounded-3xl bg-gradient-to-br ${slide.gradient} mb-6`}>
                      <Icon className="h-12 w-12 text-foreground" strokeWidth={1.5} />
                      {slide.secondaryIcons?.map((SecIcon, j) => (
                        <div
                          key={j}
                          className="absolute rounded-xl bg-background border border-border/50 p-1.5 shadow-sm"
                          style={{
                            top: j === 0 ? '-8px' : 'auto',
                            bottom: j === 1 ? '-8px' : 'auto',
                            right: j === 0 ? '-12px' : 'auto',
                            left: j === 1 ? '-12px' : 'auto',
                          }}
                        >
                          <SecIcon className="h-4 w-4 text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                    <h3 className="text-lg font-semibold text-center mb-2">{slide.title}</h3>
                    <p className="text-sm text-muted-foreground text-center leading-relaxed max-w-xs">
                      {slide.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 px-8 pb-8">
          <div className="flex gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentSlide ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'
                }`}
                onClick={() => emblaApi?.scrollTo(i)}
              />
            ))}
          </div>

          <div className="flex items-center gap-3 w-full">
            {canScrollPrev && (
              <Button variant="outline" size="sm" onClick={() => emblaApi?.scrollPrev()}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            <div className="flex-1" />
            {isLastSlide ? (
              <Button size="sm" onClick={handleDismiss}>
                Get Started <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button size="sm" onClick={() => emblaApi?.scrollNext()}>
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
